import './running_tasks.style.scss';

import { Input, Select, Table, Tag, Tooltip, Typography } from 'antd';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import FilterDropdown from '../../foundation/components/filter_dropdown/FilterDropdown';
import { useTableFilters } from '../../foundation/hooks';
import { useGetRunningTasksFilterOptionsQuery } from '../api/variantApi';
import { selectUsername } from '../authentication/redux/selectors';
import type { RunningTask } from '../variant_workflow/redux';
import {
  RUNNING_TASK_LABELS,
  RUNNING_TASK_STATUS_TAG_COLORS,
} from './constants';
import { useRunningTasks } from './hooks/useRunningTasks';

const formatDateTime = (value?: string | null) =>
  value ? new Date(value).toLocaleString() : '-';

const TaskVariantCell = ({ task }: { task: RunningTask }) => {
  if (!task.variant_id) {
    return <span>{task.description}</span>;
  }

  return (
    <Link to={`/variant-workflow/${task.variant_id}`}>
      {task.variant_id} ({task.variant_hgvs})
    </Link>
  );
};

const ErrorMessageCell = ({
  errorMessage,
}: {
  errorMessage?: string | null;
}) => {
  if (!errorMessage) {
    return <span>-</span>;
  }

  return (
    <Tooltip
      title={errorMessage}
      overlayClassName="running-tasks-page__error-tooltip"
    >
      <span className="running-tasks-page__error-text">{errorMessage}</span>
    </Tooltip>
  );
};

const RunningTasks = () => {
  const username = useSelector(selectUsername);
  const { data: filterOptionsData } = useGetRunningTasksFilterOptionsQuery();

  // Same column-filter pattern as the variant/sample search tables:
  // selection lives in useTableFilters and is sent to the backend, which
  // refetches the filtered list. Defaults to the logged-in user.
  const { columnFilters, handleFilterChange, handleFilterClear } =
    useTableFilters({
      initialFilters: { triggered_by: username ? [username] : [] },
    });

  const {
    filteredTasks,
    isLoading,
    availableTaskTypes,
    statusFilter,
    setStatusFilter,
    taskTypeFilter,
    setTaskTypeFilter,
    search,
    setSearch,
  } = useRunningTasks(columnFilters.triggered_by);

  // Ensure the current user is selectable even before they have any tasks.
  const triggeredByOptions = useMemo(() => {
    const options = filterOptionsData?.triggered_by ?? [];
    if (username && !options.includes(username)) {
      return [...options, username].sort();
    }
    return options;
  }, [filterOptionsData, username]);

  const taskColumns = [
    {
      title: 'Task',
      dataIndex: 'task_type',
      render: (taskType: string) => RUNNING_TASK_LABELS[taskType] ?? taskType,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status: RunningTask['status']) => (
        <Tag color={RUNNING_TASK_STATUS_TAG_COLORS[status]}>{status}</Tag>
      ),
    },
    {
      title: (
        <span>
          Triggered by
          <FilterDropdown
            columnKey="triggered_by"
            title="Triggered by"
            filterOptions={triggeredByOptions}
            selectedValues={columnFilters.triggered_by}
            onFilterChange={(values) =>
              handleFilterChange('triggered_by', values)
            }
            onClear={() => handleFilterClear('triggered_by')}
          />
        </span>
      ),
      dataIndex: 'username',
    },
    {
      title: 'Variant',
      render: (_: unknown, task: RunningTask) => (
        <TaskVariantCell task={task} />
      ),
    },
    {
      title: 'Started',
      dataIndex: 'created',
      render: (value: string) => formatDateTime(value),
    },
    {
      title: 'Finished',
      dataIndex: 'completed_at',
      render: (value: string | null | undefined) => formatDateTime(value),
    },
    {
      title: 'Error',
      dataIndex: 'error_message',
      render: (errorMessage: string | null | undefined) => (
        <ErrorMessageCell errorMessage={errorMessage} />
      ),
    },
  ];

  return (
    <div className="running-tasks-page">
      <Typography.Title level={2}>Task Queue</Typography.Title>
      <div className="running-tasks-page__filters">
        <Select
          className="running-tasks-page__filter"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'running', label: 'Running' },
            { value: 'completed', label: 'Completed' },
            { value: 'failed', label: 'Failed' },
          ]}
        />
        <Select
          className="running-tasks-page__filter"
          value={taskTypeFilter}
          onChange={setTaskTypeFilter}
          options={[
            { value: 'all', label: 'All task types' },
            ...availableTaskTypes.map((taskType) => ({
              value: taskType,
              label: RUNNING_TASK_LABELS[taskType] ?? taskType,
            })),
          ]}
        />
        <Input
          className="running-tasks-page__filter"
          allowClear
          placeholder="Search (task ID, variant, user, description)"
          value={search.freeText}
          onChange={(event) =>
            setSearch((previous) => ({
              ...previous,
              freeText: event.target.value,
            }))
          }
        />
        <Input
          className="running-tasks-page__filter"
          allowClear
          placeholder="Error messages contain…"
          value={search.errorText}
          onChange={(event) =>
            setSearch((previous) => ({
              ...previous,
              errorText: event.target.value,
            }))
          }
        />
      </div>
      <Table
        rowKey="task_id"
        loading={isLoading}
        dataSource={filteredTasks}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        columns={taskColumns}
      />
    </div>
  );
};

export default RunningTasks;
